// src/pages/AdminPage.jsx
import { useState, useEffect } from "react";
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
import { collection, query, doc, addDoc, updateDoc, deleteDoc, onSnapshot, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { generateDoubleElimMatches, isDoubleElimSupported, SUPPORTED_DOUBLE_ELIM_SIZES } from "../lib/doubleElimination";
import {
  FaTrophy, FaSignOutAlt, FaSave, FaPlus, FaTrash, FaTimes,
  FaUsers, FaProjectDiagram, FaBolt, FaEye, FaEyeSlash, FaList,
  FaGavel, FaIdBadge, FaTwitch, FaBullhorn,
} from "react-icons/fa";
import DraftWarsPanel from "./DraftWars/DraftWarsPanel";
import RosterManager from "./Admin/RosterManager";
import StreamManager from "./Admin/StreamManager";
import AnnouncementsManager from "./Admin/AnnouncementsManager";

// ── Format helpers ────────────────────────────────────────────────────────────
const isEliminationFormat = (f) => !["warzone_trios", "warzone_duos"].includes(f || "1v1");
const isCDLFormat = (f) => ["cdl_3v3", "cdl_4v4"].includes(f || "");
const parseParticipant = (str) => {
  if (!str) return { teamName: "", roster: [] };
  const [teamName, rosterStr] = str.split("|");
  return { teamName: teamName || str, roster: rosterStr ? rosterStr.split(",").filter(Boolean) : [] };
};
const encodeParticipant = (teamName, roster) =>
  roster && roster.filter(Boolean).length > 0
    ? `${teamName}|${roster.filter(Boolean).join(",")}`
    : teamName;
const calcPoints = (placement, kills) => {
  const pts = [12, 9, 7, 5, 4, 3, 2, 1][(placement || 1) - 1] ?? 0;
  return pts + (Number(kills) || 0);
};
const rosterSize = (f) => f === "cdl_4v4" ? 4 : f === "warzone_duos" ? 2 : 3;

// ─────────────────────────────────────────────
// Login Screen
// ─────────────────────────────────────────────
function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch {
      setError("Invalid email or password.");
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors";

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-gray-900 border border-gray-800 rounded-2xl p-8">
        <div className="text-center mb-8">
          <FaTrophy className="text-red-600 mx-auto mb-4" size={40} />
          <h1 className="text-2xl font-black uppercase text-white tracking-wide">Admin Panel</h1>
          <p className="text-gray-500 text-sm mt-1">Prime Valor</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="admin@example.com" required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Password</label>
            <div className="relative">
              <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass + " pr-12"} placeholder="••••••••" required />
              <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors">
                {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>
          </div>
          {error && <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">{error}</p>}
          <button type="submit" disabled={loading} className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold uppercase tracking-wide rounded-lg transition-colors">
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Match Editor Card
// ─────────────────────────────────────────────
function MatchEditor({ match, tournamentId, participants, onMatchSaved, formatType }) {
  const [form, setForm] = useState({ ...match });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => { setForm({ ...match }); }, [match]);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));
  const cdl = isCDLFormat(formatType);

  const writeToFirestore = async (overrides = {}) => {
    const merged = { ...form, ...overrides };
    const data = {
      team1Name: merged.team1Name || "",
      team1Score: merged.team1Score === "" ? 0 : Number(merged.team1Score),
      team2Name: merged.team2Name || "",
      team2Score: merged.team2Score === "" ? 0 : Number(merged.team2Score),
      winner: merged.winner || "",
      status: merged.status || "pending",
    };
    if (cdl) {
      data.team1Roster = merged.team1Roster || [];
      data.team2Roster = merged.team2Roster || [];
    }
    await updateDoc(doc(db, "tournaments", tournamentId, "matches", match.id), data);
    return merged;
  };

  const handleQuickWin = async (winnerName) => {
    if (!winnerName) return;
    setSaving(true);
    try {
      const updated = await writeToFirestore({ winner: winnerName, status: "completed" });
      setForm(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (onMatchSaved) onMatchSaved({ ...updated, id: match.id, round: match.round, matchNumber: match.matchNumber });
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await writeToFirestore();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (onMatchSaved) onMatchSaved({ ...form, id: match.id, round: match.round, matchNumber: match.matchNumber });
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  const handleToggleLive = async () => {
    const newStatus = form.status === "live" ? "pending" : "live";
    set("status", newStatus);
    try { await updateDoc(doc(db, "tournaments", tournamentId, "matches", match.id), { status: newStatus }); }
    catch (err) { console.error(err); }
  };

  const handleReset = async () => {
    const reset = { ...form, winner: "", status: "pending" };
    setForm(reset);
    try { await updateDoc(doc(db, "tournaments", tournamentId, "matches", match.id), { winner: "", status: "pending" }); }
    catch (err) { console.error(err); }
  };

  const handleDelete = async () => {
    await deleteDoc(doc(db, "tournaments", tournamentId, "matches", match.id));
  };

  const ic = "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-red-500 transition-colors";
  const scoreIc = "w-14 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm text-center focus:outline-none focus:border-red-500 transition-colors";

  // Parse participant team names for dropdown
  const teamNames = participants.map((p) => parseParticipant(p).teamName);

  const nameInput = (field, placeholder) =>
    teamNames.length > 0 ? (
      <select className={ic} value={form[field] || ""} onChange={(e) => set(field, e.target.value)}>
        <option value="">-- {placeholder} --</option>
        {teamNames.map((name, i) => <option key={i} value={name}>{name}</option>)}
      </select>
    ) : (
      <input className={ic} placeholder={placeholder} value={form[field] || ""} onChange={(e) => set(field, e.target.value)} />
    );

  const t1 = form.team1Name || "";
  const t2 = form.team2Name || "";
  const t1Won = form.winner === t1 && t1 !== "" && form.winner !== "";
  const t2Won = form.winner === t2 && t2 !== "" && form.winner !== "";
  const isLive = form.status === "live";
  const isCompleted = form.status === "completed";
  const scoreLabel = cdl ? "Maps" : "Score";

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3 relative">
      {/* Delete */}
      {!confirmDelete ? (
        <button onClick={() => setConfirmDelete(true)} className="absolute top-3 right-3 text-gray-600 hover:text-red-500 transition-colors" title="Delete match">
          <FaTrash size={12} />
        </button>
      ) : (
        <div className="absolute top-2 right-2 flex items-center gap-1">
          <span className="text-xs text-red-400">Delete?</span>
          <button onClick={handleDelete} className="text-xs text-red-500 font-bold px-1">Yes</button>
          <button onClick={() => setConfirmDelete(false)} className="text-xs text-gray-500 font-bold px-1">No</button>
        </div>
      )}

      <p className="text-xs text-gray-600 uppercase tracking-widest pr-8">
        {match.round === 0 ? "Play-In" : `Round ${match.round}`} · Match {match.matchNumber}
        {cdl && <span className="ml-2 text-red-600/60">CDL · {scoreLabel}</span>}
      </p>

      {/* Team 1 */}
      <div className={`flex items-center gap-2 rounded-lg px-2 py-1.5 -mx-1 transition-colors ${t1Won ? "bg-green-900/20 border border-green-700/30" : "border border-transparent"}`}>
        <div className="flex-1 min-w-0">{nameInput("team1Name", "Team 1")}</div>
        <input type="number" className={scoreIc} placeholder="0" title={scoreLabel}
          value={form.team1Score ?? ""} onChange={(e) => set("team1Score", e.target.value)} min="0" />
        <button onClick={() => handleQuickWin(t1)} disabled={saving || !t1}
          className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${t1Won ? "bg-green-700 text-white border border-green-600" : "bg-gray-800 border border-gray-700 text-gray-400 hover:border-green-600 hover:text-green-400 disabled:opacity-40 disabled:cursor-not-allowed"}`}>
          {t1Won ? "✓ Won" : "WIN"}
        </button>
      </div>

      {/* Team 1 roster (CDL only) */}
      {cdl && (
        <input className={`${ic} text-xs`} placeholder="Team 1 roster (comma-separated)"
          value={(form.team1Roster || []).join(", ")}
          onChange={(e) => set("team1Roster", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} />
      )}

      {/* Team 2 */}
      <div className={`flex items-center gap-2 rounded-lg px-2 py-1.5 -mx-1 transition-colors ${t2Won ? "bg-green-900/20 border border-green-700/30" : "border border-transparent"}`}>
        <div className="flex-1 min-w-0">{nameInput("team2Name", "Team 2")}</div>
        <input type="number" className={scoreIc} placeholder="0" title={scoreLabel}
          value={form.team2Score ?? ""} onChange={(e) => set("team2Score", e.target.value)} min="0" />
        <button onClick={() => handleQuickWin(t2)} disabled={saving || !t2}
          className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${t2Won ? "bg-green-700 text-white border border-green-600" : "bg-gray-800 border border-gray-700 text-gray-400 hover:border-green-600 hover:text-green-400 disabled:opacity-40 disabled:cursor-not-allowed"}`}>
          {t2Won ? "✓ Won" : "WIN"}
        </button>
      </div>

      {/* Team 2 roster (CDL only) */}
      {cdl && (
        <input className={`${ic} text-xs`} placeholder="Team 2 roster (comma-separated)"
          value={(form.team2Roster || []).join(", ")}
          onChange={(e) => set("team2Roster", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} />
      )}

      {/* Bottom controls */}
      <div className="flex items-center gap-2 pt-0.5">
        <button onClick={handleToggleLive} disabled={saving || isCompleted}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all disabled:opacity-40 ${isLive ? "bg-green-700/30 border border-green-600/50 text-green-400" : "bg-gray-800 border border-gray-700 text-gray-500 hover:border-green-600/50 hover:text-green-400"}`}>
          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isLive ? "bg-green-500 animate-pulse" : "bg-gray-600"}`} />
          {isLive ? "Live" : "Set Live"}
        </button>
        <button onClick={handleReset} disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide bg-gray-800 border border-gray-700 text-gray-500 hover:border-gray-500 hover:text-white transition-all disabled:opacity-40">
          ↺ Reset
        </button>
        <button onClick={handleSave} disabled={saving}
          className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${saved ? "bg-green-700/30 border border-green-600/50 text-green-400" : "bg-gray-800 border border-gray-700 text-gray-400 hover:border-red-600/50 hover:text-white disabled:opacity-40"}`}>
          <FaSave size={10} />
          {saving ? "..." : saved ? "Saved!" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Tournament Form
// ─────────────────────────────────────────────
function TournamentForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || {
    title: "", date: "", game: "", format: "", formatType: "1v1",
    eliminationType: "single",
    prizePool: "", status: "upcoming", isActive: false, participants: [],
  });
  const [saving, setSaving] = useState(false);
  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  const ic = "w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-red-500 transition-colors placeholder-gray-600";

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 space-y-3">
      <input className={ic} placeholder="Tournament title *" value={form.title} onChange={(e) => set("title", e.target.value)} />
      <div className="grid grid-cols-2 gap-3">
        <input className={ic} placeholder="Date (e.g. March 2026)" value={form.date} onChange={(e) => set("date", e.target.value)} />
        <input className={ic} placeholder="Game (e.g. CoD: BO6)" value={form.game} onChange={(e) => set("game", e.target.value)} />
        <select className={ic} value={form.formatType} onChange={(e) => set("formatType", e.target.value)}>
          <option value="1v1">1v1</option>
          <option value="cdl_3v3">CDL 3v3 – Team, Maps Scoring</option>
          <option value="cdl_4v4">CDL 4v4 – Team, Maps Scoring</option>
          <option value="warzone_trios">Warzone Trios – Leaderboard</option>
          <option value="warzone_duos">Warzone Duos – Leaderboard</option>
        </select>
        <input className={ic} placeholder="Prize pool (e.g. $500)" value={form.prizePool} onChange={(e) => set("prizePool", e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <select className={ic} value={form.status} onChange={(e) => set("status", e.target.value)}>
          <option value="upcoming">Upcoming</option>
          <option value="live">Live</option>
          <option value="completed">Completed</option>
        </select>
        <label className="flex items-center gap-2 px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer">
          <input type="checkbox" checked={form.isActive} onChange={(e) => set("isActive", e.target.checked)} className="accent-red-600 w-4 h-4" />
          <span className="text-sm text-gray-300">Set as Active</span>
        </label>
      </div>

      {/* Elimination type — only for bracket formats (not Warzone leaderboards) */}
      {!["warzone_trios", "warzone_duos"].includes(form.formatType) && (
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide">Elimination Style</label>
          <select className={ic} value={form.eliminationType || "single"}
            onChange={(e) => set("eliminationType", e.target.value)}>
            <option value="single">Single Elimination — one loss and you're out</option>
            <option value="double">Double Elimination — winners + losers brackets (4 or 8 teams only)</option>
          </select>
          {form.eliminationType === "double" && (
            <p className="text-xs text-amber-400/80 px-1">
              Double elim requires exactly <strong>4 or 8 teams/players</strong>. Bracket auto-generates winners + losers + grand final on creation.
            </p>
          )}
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <button onClick={handleSave} disabled={saving || !form.title.trim()} className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold text-sm uppercase rounded-lg transition-colors">
          {saving ? "Saving..." : "Save Tournament"}
        </button>
        {onCancel && <button onClick={onCancel} className="px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors">Cancel</button>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main AdminPage
// ─────────────────────────────────────────────
function AdminPage() {
  const [user, setUser] = useState(undefined);
  // "roster" | "tournaments" | "draftwars"
  // Roster is the default — it's the day-to-day task. Remove the "draftwars"
  // option after the Draft Wars event ends.
  const [adminMode, setAdminMode] = useState("roster");
  const [tournaments, setTournaments] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [matches, setMatches] = useState([]);
  const [warzoneRounds, setWarzoneRounds] = useState([]);
  const [editingRound, setEditingRound] = useState(null); // { roundNumber, rows: [] }
  const [activeTab, setActiveTab] = useState("participants");

  const [showNewTournament, setShowNewTournament] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState("");
  const [newTeamForm, setNewTeamForm] = useState({ name: "", players: ["", "", "", ""] });
  const [addingMatch, setAddingMatch] = useState(false);
  const [newMatch, setNewMatch] = useState({ round: 1, matchNumber: 1, team1Name: "", team2Name: "" });
  const [generating, setGenerating] = useState(false);
  const [autoAdvanceMsg, setAutoAdvanceMsg] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u ?? null));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "tournaments"));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.title || "").localeCompare(b.title || ""));
      setTournaments(list);
      if (!selectedId) {
        const active = list.find((t) => t.isActive);
        if (active) setSelectedId(active.id);
      }
    });
    return () => unsub();
  }, [user]); // eslint-disable-line

  // Matches subscription (elimination formats)
  useEffect(() => {
    if (!selectedId) { setMatches([]); return; }
    const ft = tournaments.find((t) => t.id === selectedId)?.formatType || "1v1";
    if (!isEliminationFormat(ft)) { setMatches([]); return; }
    const q = query(collection(db, "tournaments", selectedId, "matches"));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.round ?? 0) - (b.round ?? 0) || (a.matchNumber ?? 0) - (b.matchNumber ?? 0));
      setMatches(list);
    });
    return () => unsub();
  }, [selectedId, tournaments]);

  // Warzone rounds subscription
  useEffect(() => {
    if (!selectedId) { setWarzoneRounds([]); return; }
    const ft = tournaments.find((t) => t.id === selectedId)?.formatType || "1v1";
    if (isEliminationFormat(ft)) { setWarzoneRounds([]); return; }
    const q = query(collection(db, "tournaments", selectedId, "rounds"));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => a.roundNumber - b.roundNumber);
      setWarzoneRounds(list);
    });
    return () => unsub();
  }, [selectedId, tournaments]);

  const selectedTournament = tournaments.find((t) => t.id === selectedId);
  const participants = selectedTournament?.participants || [];
  const formatType = selectedTournament?.formatType || "1v1";
  const eliminationType = selectedTournament?.eliminationType || "single";
  const isElimination = isEliminationFormat(formatType);
  const isCDL = isCDLFormat(formatType);
  const isDoubleElim = isElimination && eliminationType === "double";
  const maxRoster = rosterSize(formatType);

  // ── Participants handlers ──
  const handleAddPlayer = async () => {
    const name = newPlayerName.trim();
    if (!name || !selectedId) return;
    if (participants.includes(name)) { setNewPlayerName(""); return; }
    await updateDoc(doc(db, "tournaments", selectedId), { participants: [...participants, name] });
    setNewPlayerName("");
  };

  const handleAddTeam = async () => {
    const name = newTeamForm.name.trim();
    if (!name || !selectedId) return;
    const encoded = encodeParticipant(name, newTeamForm.players.slice(0, maxRoster));
    await updateDoc(doc(db, "tournaments", selectedId), { participants: [...participants, encoded] });
    setNewTeamForm({ name: "", players: ["", "", "", ""] });
  };

  const handleRemovePlayer = async (p) => {
    await updateDoc(doc(db, "tournaments", selectedId), { participants: participants.filter((x) => x !== p) });
  };

  // ── Generate bracket ──
  const handleGenerateBracket = async () => {
    if (participants.length < 2 || !selectedId) return;
    setGenerating(true);
    try {
      const teamNames = participants.map((p) => parseParticipant(p).teamName);
      const teamRosters = participants.map((p) => parseParticipant(p).roster);

      // ── Double Elimination branch ──
      if (isDoubleElim) {
        if (!isDoubleElimSupported(teamNames.length)) {
          alert(`Double elimination requires exactly ${SUPPORTED_DOUBLE_ELIM_SIZES.join(" or ")} teams (you have ${teamNames.length}).`);
          setGenerating(false);
          return;
        }
        // Wipe any existing matches first
        if (matches.length > 0) {
          if (!window.confirm("Existing bracket will be wiped and regenerated. Continue?")) {
            setGenerating(false);
            return;
          }
          await Promise.all(matches.map((m) => deleteDoc(doc(db, "tournaments", selectedId, "matches", m.id))));
        }
        const seedParticipants = teamNames.map((name, i) => ({ teamName: name, roster: teamRosters[i] || [] }));
        const matchDocs = generateDoubleElimMatches(seedParticipants, isCDL);
        await Promise.all(
          matchDocs.map((m) => addDoc(collection(db, "tournaments", selectedId, "matches"), m))
        );
        setAutoAdvanceMsg(`⚡ Double elim bracket created — ${matchDocs.length} matches across WB / LB / Grand Final.`);
        setTimeout(() => setAutoAdvanceMsg(""), 6000);
        setActiveTab("bracket");
        setGenerating(false);
        return;
      }

      // ── Single Elimination branch ──
      const isOdd = teamNames.length % 2 !== 0;

      const makeMatchDoc = (round, matchNumber, i1, i2, winner = "", status = "pending") => ({
        round, matchNumber,
        team1Name: teamNames[i1], team1Score: 0,
        team2Name: teamNames[i2], team2Score: 0,
        ...(isCDL ? { team1Roster: teamRosters[i1] || [], team2Roster: teamRosters[i2] || [] } : {}),
        winner, status,
      });

      if (isOdd) {
        const playInExists = matches.some((m) => m.round === 0);
        if (playInExists) {
          if (!window.confirm("A Play-In match already exists. Delete and regenerate?")) { setGenerating(false); return; }
          await Promise.all(matches.filter((m) => m.round === 0).map((m) => deleteDoc(doc(db, "tournaments", selectedId, "matches", m.id))));
        }
        const round1Matches = matches.filter((m) => m.round === 1);
        if (round1Matches.length > 0) {
          await Promise.all(round1Matches.map((m) => deleteDoc(doc(db, "tournaments", selectedId, "matches", m.id))));
        }
        const last = teamNames.length - 1;
        await addDoc(collection(db, "tournaments", selectedId, "matches"), makeMatchDoc(0, 1, last - 1, last));
        setAutoAdvanceMsg("⚡ Play-In match created! Complete it to auto-generate Round 1.");
        setTimeout(() => setAutoAdvanceMsg(""), 6000);
        setActiveTab("bracket");
      } else {
        const round1Exists = matches.some((m) => m.round === 1);
        if (round1Exists) {
          if (!window.confirm("Round 1 matches already exist. Delete them and regenerate?")) { setGenerating(false); return; }
          await Promise.all(matches.filter((m) => m.round === 1).map((m) => deleteDoc(doc(db, "tournaments", selectedId, "matches", m.id))));
        }
        const promises = [];
        for (let i = 0; i < teamNames.length - 1; i += 2) {
          promises.push(addDoc(collection(db, "tournaments", selectedId, "matches"), makeMatchDoc(1, Math.floor(i / 2) + 1, i, i + 1)));
        }
        await Promise.all(promises);
        setActiveTab("bracket");
      }
    } finally { setGenerating(false); }
  };

  // ── Add match manually ──
  const handleAddMatch = async () => {
    if (!selectedId) return;
    await addDoc(collection(db, "tournaments", selectedId, "matches"), {
      round: Number(newMatch.round) || 1,
      matchNumber: Number(newMatch.matchNumber) || 1,
      team1Name: newMatch.team1Name || "",
      team1Score: 0,
      team2Name: newMatch.team2Name || "",
      team2Score: 0,
      winner: "",
      status: "pending",
    });
    setAddingMatch(false);
    setNewMatch({ round: 1, matchNumber: 1, team1Name: "", team2Name: "" });
  };

  const handleCreateTournament = async (form) => {
    const ref = doc(collection(db, "tournaments"));
    await setDoc(ref, { ...form, participants: [] });
    setShowNewTournament(false);
    setSelectedId(ref.id);
  };

  const handleToggleActive = async () => {
    if (!selectedId) return;
    const isCurrentlyActive = selectedTournament?.isActive;
    await Promise.all(tournaments.map((t) => updateDoc(doc(db, "tournaments", t.id), { isActive: false })));
    if (!isCurrentlyActive) await updateDoc(doc(db, "tournaments", selectedId), { isActive: true });
  };

  // ── Auto-advance winners to next round ──
  const checkAndAdvanceRound = async (completedRound, currentMatches) => {
    const roundMatches = currentMatches.filter((m) => m.round === completedRound);
    const allDone = roundMatches.length > 0 && roundMatches.every((m) => m.winner && m.winner !== "");
    if (!allDone) return;
    const nextRound = completedRound + 1;
    const nextExists = currentMatches.some((m) => m.round === nextRound);
    if (nextExists) return;

    const makePairedDoc = (round, matchNumber, t1Name, t2Name, t1Roster = [], t2Roster = []) => ({
      round, matchNumber,
      team1Name: t1Name, team1Score: 0,
      team2Name: t2Name, team2Score: 0,
      ...(isCDL ? { team1Roster: t1Roster, team2Roster: t2Roster } : {}),
      winner: "", status: "pending",
    });

    const makeByeDoc = (round, matchNumber, winnerName, winnerRoster = []) => ({
      round, matchNumber,
      team1Name: winnerName, team1Score: 0,
      team2Name: "BYE", team2Score: 0,
      ...(isCDL ? { team1Roster: winnerRoster, team2Roster: [] } : {}),
      winner: winnerName, status: "completed",
    });

    if (completedRound === 0) {
      const playInTeams = new Set();
      roundMatches.forEach((m) => { if (m.team1Name) playInTeams.add(m.team1Name); if (m.team2Name) playInTeams.add(m.team2Name); });
      const playInWinners = roundMatches.map((m) => m.winner).filter(Boolean);
      const directSeeds = participants.map((p) => parseParticipant(p).teamName).filter((n) => !playInTeams.has(n));
      const round1Teams = [...directSeeds, ...playInWinners];
      const promises = [];
      for (let i = 0; i < round1Teams.length - 1; i += 2) {
        const t1Roster = isCDL ? (parseParticipant(participants.find((p) => parseParticipant(p).teamName === round1Teams[i]) || "")?.roster || []) : [];
        const t2Roster = isCDL ? (parseParticipant(participants.find((p) => parseParticipant(p).teamName === round1Teams[i + 1]) || "")?.roster || []) : [];
        promises.push(addDoc(collection(db, "tournaments", selectedId, "matches"), makePairedDoc(1, Math.floor(i / 2) + 1, round1Teams[i], round1Teams[i + 1], t1Roster, t2Roster)));
      }
      if (round1Teams.length % 2 !== 0) {
        const bye = round1Teams[round1Teams.length - 1];
        promises.push(addDoc(collection(db, "tournaments", selectedId, "matches"), makeByeDoc(1, Math.ceil(round1Teams.length / 2), bye)));
      }
      await Promise.all(promises);
      setAutoAdvanceMsg("⚡ Round 1 generated from Play-In results!");
      setTimeout(() => setAutoAdvanceMsg(""), 5000);
      return;
    }

    const winners = [...roundMatches].sort((a, b) => (a.matchNumber ?? 0) - (b.matchNumber ?? 0)).map((m) => m.winner);
    if (winners.length <= 1) return;
    const promises = [];
    for (let i = 0; i < winners.length - 1; i += 2) {
      const t1Roster = isCDL ? (currentMatches.find((m) => m.winner === winners[i] && m.round === completedRound)?.team1Name === winners[i]
        ? currentMatches.find((m) => m.winner === winners[i] && m.round === completedRound)?.team1Roster
        : currentMatches.find((m) => m.winner === winners[i] && m.round === completedRound)?.team2Roster) || [] : [];
      const t2Roster = isCDL ? (currentMatches.find((m) => m.winner === winners[i + 1] && m.round === completedRound)?.team1Name === winners[i + 1]
        ? currentMatches.find((m) => m.winner === winners[i + 1] && m.round === completedRound)?.team1Roster
        : currentMatches.find((m) => m.winner === winners[i + 1] && m.round === completedRound)?.team2Roster) || [] : [];
      promises.push(addDoc(collection(db, "tournaments", selectedId, "matches"), makePairedDoc(nextRound, Math.floor(i / 2) + 1, winners[i], winners[i + 1], t1Roster, t2Roster)));
    }
    if (winners.length % 2 !== 0) {
      const bye = winners[winners.length - 1];
      promises.push(addDoc(collection(db, "tournaments", selectedId, "matches"), makeByeDoc(nextRound, Math.ceil(winners.length / 2), bye)));
    }
    await Promise.all(promises);
    setAutoAdvanceMsg(`⚡ Round ${nextRound} generated automatically!`);
    setTimeout(() => setAutoAdvanceMsg(""), 5000);
  };

  const handleMatchSaved = (savedForm) => {
    if (!savedForm.winner || savedForm.status !== "completed") return;
    const updatedMatches = matches.map((m) => m.id === savedForm.id ? { ...m, ...savedForm } : m);

    // ── Double-elim routing: each match has winnerAdvancesTo + loserDropsTo ──
    if (isDoubleElim) {
      const justSaved = updatedMatches.find((m) => m.id === savedForm.id);
      if (!justSaved) return;
      // Determine loser
      const loserName = justSaved.team1Name === savedForm.winner ? justSaved.team2Name : justSaved.team1Name;
      const loserRoster = isCDL ? (justSaved.team1Name === savedForm.winner ? justSaved.team2Roster : justSaved.team1Roster) : null;
      const winnerRoster = isCDL ? (justSaved.team1Name === savedForm.winner ? justSaved.team1Roster : justSaved.team2Roster) : null;

      // Helper: find target match by routing pointer + write the team into the right slot
      const fillSlot = async (target, teamName, roster) => {
        if (!target || !teamName) return;
        const tgt = updatedMatches.find((m) =>
          m.bracket === target.bracket && m.round === target.round && m.matchNumber === target.matchNumber
        );
        if (!tgt) return;
        const data = {};
        if (target.slot === "team1") {
          data.team1Name = teamName;
          if (isCDL) data.team1Roster = roster || [];
        } else {
          data.team2Name = teamName;
          if (isCDL) data.team2Roster = roster || [];
        }
        await updateDoc(doc(db, "tournaments", selectedId, "matches", tgt.id), data);
      };

      // Route winner + loser
      Promise.all([
        fillSlot(justSaved.winnerAdvancesTo, savedForm.winner, winnerRoster),
        fillSlot(justSaved.loserDropsTo, loserName, loserRoster),
      ]).catch((err) => console.error("Double-elim routing error:", err));
      return;
    }

    // ── Single-elim auto-advance (existing logic) ──
    checkAndAdvanceRound(savedForm.round, updatedMatches);
  };

  // ── Warzone standings handlers ──
  const handleStartNewRound = () => {
    const roundNumber = warzoneRounds.length + 1;
    const rows = participants.map((p) => {
      const parsed = parseParticipant(p);
      return { teamName: parsed.teamName, roster: parsed.roster, placement: "", kills: "" };
    });
    setEditingRound({ roundNumber, rows });
  };

  const handleSaveRound = async () => {
    if (!editingRound || !selectedId) return;
    await addDoc(collection(db, "tournaments", selectedId, "rounds"), {
      roundNumber: editingRound.roundNumber,
      standings: editingRound.rows.map((r) => ({
        teamName: r.teamName,
        roster: r.roster || [],
        placement: Number(r.placement) || 0,
        kills: Number(r.kills) || 0,
        points: calcPoints(Number(r.placement) || 0, Number(r.kills) || 0),
      })),
    });
    setEditingRound(null);
  };

  const handleDeleteRound = async (roundId) => {
    if (!window.confirm("Delete this round?")) return;
    await deleteDoc(doc(db, "tournaments", selectedId, "rounds", roundId));
  };

  const matchesByRound = matches.reduce((acc, m) => {
    if (!acc[m.round]) acc[m.round] = [];
    acc[m.round].push(m);
    return acc;
  }, {});

  // For double-elim: also group by bracket
  const matchesByBracket = matches.reduce((acc, m) => {
    const b = m.bracket || "WB";
    if (!acc[b]) acc[b] = {};
    if (!acc[b][m.round]) acc[b][m.round] = [];
    acc[b][m.round].push(m);
    return acc;
  }, {});

  if (user === undefined) return <div className="min-h-screen bg-black flex items-center justify-center"><p className="text-gray-500 animate-pulse">Loading...</p></div>;
  if (!user) return <LoginForm />;

  const ic = "px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-red-500 transition-colors placeholder-gray-600";

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gray-950 border-b border-gray-800 px-6 py-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <FaTrophy className="text-red-600" />
          <span className="font-black uppercase text-sm tracking-widest">Admin Panel</span>
          <span className="text-gray-600 text-sm hidden sm:block">— Prime Valor</span>
        </div>
        <button onClick={() => signOut(auth)} className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white text-sm rounded-lg transition-colors">
          <FaSignOutAlt size={14} /> Sign Out
        </button>
      </div>

      {/* ── Mode Toggle (Tournaments vs Draft Wars) ── */}
      {/* TO REMOVE DRAFT WARS AFTER EVENT: delete this whole block + the draftwars conditional below */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
        <div className="inline-flex bg-gray-900 border border-gray-800 rounded-xl p-1">
          <button onClick={() => setAdminMode("roster")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${
              adminMode === "roster"
                ? "bg-red-600 text-white"
                : "text-gray-400 hover:text-white"
            }`}>
            <FaIdBadge size={11} /> Roster
          </button>
          <button onClick={() => setAdminMode("announcements")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${
              adminMode === "announcements"
                ? "bg-red-600 text-white"
                : "text-gray-400 hover:text-white"
            }`}>
            <FaBullhorn size={11} /> Announcements
          </button>
          <button onClick={() => setAdminMode("stream")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${
              adminMode === "stream"
                ? "bg-red-600 text-white"
                : "text-gray-400 hover:text-white"
            }`}>
            <FaTwitch size={11} /> Live Stream
          </button>
          <button onClick={() => setAdminMode("tournaments")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${
              adminMode === "tournaments"
                ? "bg-red-600 text-white"
                : "text-gray-400 hover:text-white"
            }`}>
            <FaTrophy size={11} /> Tournaments
          </button>
          <button onClick={() => setAdminMode("draftwars")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${
              adminMode === "draftwars"
                ? "bg-red-600 text-white"
                : "text-gray-400 hover:text-white"
            }`}>
            <FaGavel size={11} /> Draft Wars
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">

        {/* ── Roster Mode ── */}
        {adminMode === "roster" && <RosterManager />}

        {/* ── Announcements Mode ── */}
        {adminMode === "announcements" && <AnnouncementsManager />}

        {/* ── Live Stream Mode ── */}
        {adminMode === "stream" && <StreamManager />}

        {/* ── Draft Wars Mode ── */}
        {adminMode === "draftwars" && <DraftWarsPanel />}

        {/* ── Tournament selector ── */}
        {adminMode === "tournaments" && (<>

        <section className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold uppercase tracking-widest">Tournaments</h2>
            <button onClick={() => setShowNewTournament((v) => !v)} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold uppercase rounded-lg transition-colors">
              {showNewTournament ? <FaTimes size={12} /> : <FaPlus size={12} />}
              {showNewTournament ? "Cancel" : "New Tournament"}
            </button>
          </div>

          {showNewTournament && <TournamentForm onSave={handleCreateTournament} onCancel={() => setShowNewTournament(false)} />}

          {tournaments.length > 0 && (
            <div className="space-y-2">
              <label className="block text-xs text-gray-500 uppercase tracking-wide">Select tournament to manage</label>
              <div className="flex flex-wrap items-center gap-3">
                <select className={`flex-1 min-w-0 max-w-lg ${ic}`} value={selectedId} onChange={(e) => { setSelectedId(e.target.value); setActiveTab("participants"); setEditingRound(null); }}>
                  <option value="">-- Choose a tournament --</option>
                  {tournaments.map((t) => (
                    <option key={t.id} value={t.id}>{t.title}{t.isActive ? " ✓ ACTIVE" : ""}{t.status ? ` (${t.status})` : ""}</option>
                  ))}
                </select>
                {selectedId && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={handleToggleActive}
                      className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wide transition-colors ${selectedTournament?.isActive ? "bg-gray-700 hover:bg-gray-600 text-gray-300" : "bg-green-700 hover:bg-green-600 text-white"}`}>
                      {selectedTournament?.isActive ? "Deactivate (Hide)" : "Set Live (Show)"}
                    </button>
                    <select value={selectedTournament?.status || "upcoming"}
                      onChange={(e) => updateDoc(doc(db, "tournaments", selectedId), { status: e.target.value })}
                      className="flex-shrink-0 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-red-500 transition-colors">
                      <option value="upcoming">Status: Upcoming</option>
                      <option value="live">Status: Live</option>
                      <option value="completed">Status: Completed</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {tournaments.length === 0 && !showNewTournament && (
            <p className="text-gray-600 text-sm">No tournaments yet. Click "New Tournament" to create one.</p>
          )}
        </section>

        {/* ── Tournament Manager ── */}
        {selectedId && selectedTournament && (
          <section className="space-y-5">

            {/* Tab bar */}
            <div className="flex gap-2 border-b border-gray-800 pb-0">
              <button onClick={() => setActiveTab("participants")}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wide border-b-2 transition-colors ${activeTab === "participants" ? "border-red-600 text-red-500" : "border-transparent text-gray-500 hover:text-white"}`}>
                <FaUsers size={13} /> {isElimination && !isCDL ? "Players" : "Teams"} ({participants.length})
              </button>
              {isElimination ? (
                <button onClick={() => setActiveTab("bracket")}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wide border-b-2 transition-colors ${activeTab === "bracket" ? "border-red-600 text-red-500" : "border-transparent text-gray-500 hover:text-white"}`}>
                  <FaProjectDiagram size={13} /> Bracket ({matches.length} matches)
                </button>
              ) : (
                <button onClick={() => setActiveTab("standings")}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wide border-b-2 transition-colors ${activeTab === "standings" ? "border-red-600 text-red-500" : "border-transparent text-gray-500 hover:text-white"}`}>
                  <FaList size={13} /> Standings ({warzoneRounds.length} rounds)
                </button>
              )}
            </div>

            {/* ── PARTICIPANTS TAB ── */}
            {activeTab === "participants" && (
              <div className="space-y-5">
                <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-4">
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-widest text-white mb-1">
                      {isElimination && !isCDL ? "Add Players" : "Add Teams"}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {isElimination && !isCDL
                        ? "Add all players before generating the bracket."
                        : `Add all teams with their rosters (up to ${maxRoster} players per team).`}
                    </p>
                  </div>

                  {/* 1v1: single player input */}
                  {isElimination && !isCDL && (
                    <div className="flex gap-2">
                      <input className={`flex-1 ${ic}`} placeholder="Enter player gamertag..."
                        value={newPlayerName} onChange={(e) => setNewPlayerName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAddPlayer()} />
                      <button onClick={handleAddPlayer} disabled={!newPlayerName.trim()}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold text-sm rounded-lg transition-colors flex items-center gap-1">
                        <FaPlus size={11} /> Add
                      </button>
                    </div>
                  )}

                  {/* CDL / Warzone: team entry form */}
                  {(!isElimination || isCDL) && (
                    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 space-y-2">
                      <input className={`${ic} w-full`} placeholder="Team name *"
                        value={newTeamForm.name} onChange={(e) => setNewTeamForm((p) => ({ ...p, name: e.target.value }))} />
                      <div className="grid grid-cols-2 gap-2">
                        {Array.from({ length: maxRoster }).map((_, i) => (
                          <input key={i} className={`${ic} w-full`} placeholder={`Player ${i + 1}`}
                            value={newTeamForm.players[i] || ""}
                            onChange={(e) => setNewTeamForm((p) => {
                              const players = [...p.players];
                              players[i] = e.target.value;
                              return { ...p, players };
                            })} />
                        ))}
                      </div>
                      <button onClick={handleAddTeam} disabled={!newTeamForm.name.trim()}
                        className="w-full py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold text-sm uppercase rounded-lg transition-colors flex items-center justify-center gap-2">
                        <FaPlus size={11} /> Add Team
                      </button>
                    </div>
                  )}

                  {/* Participants list */}
                  {participants.length > 0 ? (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">
                        {participants.length} {isElimination && !isCDL ? "player" : "team"}{participants.length !== 1 ? "s" : ""} registered
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {participants.map((p, i) => {
                          const parsed = parseParticipant(p);
                          return (
                            <div key={p} className="flex items-start gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl text-sm max-w-xs">
                              <span className="text-gray-400 text-xs w-4 text-right flex-shrink-0 mt-0.5">{i + 1}.</span>
                              <div className="min-w-0 flex-1">
                                <span className="text-white font-medium block">{parsed.teamName}</span>
                                {parsed.roster.length > 0 && (
                                  <span className="text-gray-500 text-xs block">{parsed.roster.join(", ")}</span>
                                )}
                              </div>
                              <button onClick={() => handleRemovePlayer(p)} className="text-gray-600 hover:text-red-500 transition-colors ml-1 flex-shrink-0 mt-0.5">
                                <FaTimes size={10} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-600 text-sm text-center py-4">No {isElimination && !isCDL ? "players" : "teams"} added yet.</p>
                  )}
                </div>

                {/* Generate bracket button (elimination only) */}
                {isElimination && participants.length >= 2 && (
                  <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-3">
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-widest text-white mb-1">
                        {isDoubleElim
                          ? "Generate Double-Elim Bracket"
                          : participants.length % 2 !== 0 ? "Generate Play-In + Bracket" : "Generate Round 1"}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {isDoubleElim
                          ? `Creates Winners Bracket, Losers Bracket, and Grand Final. ${isDoubleElimSupported(participants.length) ? `Ready to generate (${participants.length} teams).` : `Need ${SUPPORTED_DOUBLE_ELIM_SIZES.join(" or ")} teams — you have ${participants.length}.`}`
                          : participants.length % 2 !== 0
                          ? `Odd number (${participants.length}). Last 2 play a Play-In — winner joins the other ${participants.length - 2} in Round 1.`
                          : `Pairs all ${participants.length} ${isCDL ? "teams" : "players"} into ${participants.length / 2} Round 1 matches.`}
                      </p>
                    </div>
                    <button onClick={handleGenerateBracket} disabled={generating || (isDoubleElim && !isDoubleElimSupported(participants.length))}
                      className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wide rounded-lg transition-colors flex items-center justify-center gap-2">
                      <FaBolt size={13} />
                      {generating
                        ? "Generating..."
                        : isDoubleElim
                        ? `Generate ${participants.length === 8 ? "14" : participants.length === 4 ? "6" : "??"} Matches`
                        : participants.length % 2 !== 0 ? "Generate Play-In Match" : `Generate ${participants.length / 2} Round 1 Matches`}
                    </button>
                    <p className="text-xs text-gray-600 text-center">After generating, go to the Bracket tab to manage scores.</p>
                  </div>
                )}
              </div>
            )}

            {/* ── BRACKET TAB (elimination) ── */}
            {activeTab === "bracket" && isElimination && (
              <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-widest text-white">{selectedTournament.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {isCDL ? "Score = Maps Won · " : ""}Update scores — changes appear on the public bracket instantly
                    </p>
                  </div>
                  <button onClick={() => setAddingMatch((v) => !v)} className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-bold uppercase rounded-lg transition-colors">
                    {addingMatch ? <FaTimes size={12} /> : <FaPlus size={12} />}
                    {addingMatch ? "Cancel" : "Add Match"}
                  </button>
                </div>

                {addingMatch && (
                  <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 space-y-3">
                    <p className="text-sm font-semibold text-white">New Match</p>
                    <div className="grid grid-cols-2 gap-3">
                      {participants.length > 0 ? (
                        <>
                          <select className={ic} value={newMatch.team1Name} onChange={(e) => setNewMatch({ ...newMatch, team1Name: e.target.value })}>
                            <option value="">-- Team 1 --</option>
                            {participants.map((p, i) => { const { teamName } = parseParticipant(p); return <option key={i} value={teamName}>{teamName}</option>; })}
                          </select>
                          <select className={ic} value={newMatch.team2Name} onChange={(e) => setNewMatch({ ...newMatch, team2Name: e.target.value })}>
                            <option value="">-- Team 2 --</option>
                            {participants.map((p, i) => { const { teamName } = parseParticipant(p); return <option key={i} value={teamName}>{teamName}</option>; })}
                          </select>
                        </>
                      ) : (
                        <>
                          <input className={ic} placeholder="Team 1" value={newMatch.team1Name} onChange={(e) => setNewMatch({ ...newMatch, team1Name: e.target.value })} />
                          <input className={ic} placeholder="Team 2" value={newMatch.team2Name} onChange={(e) => setNewMatch({ ...newMatch, team2Name: e.target.value })} />
                        </>
                      )}
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Round #</label>
                        <input type="number" className={`w-full ${ic}`} value={newMatch.round} min="1" onChange={(e) => setNewMatch({ ...newMatch, round: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Match # in round</label>
                        <input type="number" className={`w-full ${ic}`} value={newMatch.matchNumber} min="1" onChange={(e) => setNewMatch({ ...newMatch, matchNumber: e.target.value })} />
                      </div>
                    </div>
                    <button onClick={handleAddMatch} disabled={!newMatch.team1Name || !newMatch.team2Name}
                      className="w-full py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold text-sm uppercase rounded-lg transition-colors">
                      Create Match
                    </button>
                  </div>
                )}

                {autoAdvanceMsg && (
                  <div className="flex items-center justify-between gap-3 px-4 py-3 bg-green-700/20 border border-green-600/40 rounded-xl text-green-400 text-sm font-semibold">
                    <span>{autoAdvanceMsg}</span>
                    <button onClick={() => setAutoAdvanceMsg("")} className="text-green-500 hover:text-white transition-colors"><FaTimes size={12} /></button>
                  </div>
                )}

                {Object.keys(matchesByRound).length === 0 && !addingMatch && (
                  <div className="text-center py-12 bg-gray-900 border border-gray-800 rounded-xl">
                    <p className="text-gray-500 mb-3">No matches yet.</p>
                    <p className="text-gray-600 text-sm">Go to the <strong className="text-gray-400">Participants</strong> tab, add all {isCDL ? "teams" : "players"}, then click Generate.</p>
                  </div>
                )}

                {/* Double Elim: render WB / LB / GF sections separately */}
                {isDoubleElim ? (
                  <>
                    {[
                      { key: "WB", label: "Winners Bracket", color: "amber" },
                      { key: "LB", label: "Losers Bracket", color: "blue" },
                      { key: "GF", label: "Grand Final", color: "red" },
                    ].map(({ key, label, color }) => {
                      const rounds = matchesByBracket[key] || {};
                      const sortedRounds = Object.keys(rounds).map(Number).sort((a, b) => a - b);
                      if (sortedRounds.length === 0) return null;
                      return (
                        <div key={key} className="space-y-4">
                          <div className="flex items-center gap-3">
                            <span className={`text-sm font-black uppercase tracking-widest text-${color}-400 border border-${color}-600/40 bg-${color}-600/10 px-4 py-1.5 rounded-full`}>
                              {label}
                            </span>
                            <div className="flex-1 h-px bg-gray-800" />
                          </div>
                          {sortedRounds.map((round) => (
                            <div key={round} className="ml-2">
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                                {(rounds[round][0]?.label) || `Round ${round}`}
                              </p>
                              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {rounds[round].map((match) => (
                                  <MatchEditor key={match.id} match={match} tournamentId={selectedId} participants={participants} onMatchSaved={handleMatchSaved} formatType={formatType} />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  Object.keys(matchesByRound).sort((a, b) => Number(a) - Number(b)).map((round) => (
                    <div key={round}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs font-bold uppercase tracking-widest text-red-500 border border-red-600/30 bg-red-600/10 px-3 py-1 rounded-full">
                          {Number(round) === 0 ? "Play-In" : `Round ${round}`}
                        </span>
                        <div className="flex-1 h-px bg-gray-800" />
                      </div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {matchesByRound[round].map((match) => (
                          <MatchEditor key={match.id} match={match} tournamentId={selectedId} participants={participants} onMatchSaved={handleMatchSaved} formatType={formatType} />
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ── STANDINGS TAB (Warzone) ── */}
            {activeTab === "standings" && !isElimination && (
              <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-widest text-white">{selectedTournament.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Points: 1st=12 · 2nd=9 · 3rd=7 · 4th=5 · 5th=4 · 6th=3 · 7th=2 · 8th=1 · + kills</p>
                  </div>
                  {!editingRound && (
                    <button onClick={handleStartNewRound} disabled={participants.length < 2}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white text-sm font-bold uppercase rounded-lg transition-colors">
                      <FaPlus size={12} /> Add Round {warzoneRounds.length + 1}
                    </button>
                  )}
                </div>

                {/* Editing new round */}
                {editingRound && (
                  <div className="bg-gray-900 border border-red-600/30 rounded-2xl p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold uppercase tracking-widest text-red-400">Round {editingRound.roundNumber} — Enter Results</h4>
                      <button onClick={() => setEditingRound(null)} className="text-gray-500 hover:text-white"><FaTimes size={14} /></button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-800">
                            <th className="text-left py-2 px-3 text-xs text-gray-500 uppercase">Team</th>
                            <th className="text-left py-2 px-3 text-xs text-gray-500 uppercase hidden sm:table-cell">Players</th>
                            <th className="text-center py-2 px-3 text-xs text-gray-500 uppercase w-24">Placement</th>
                            <th className="text-center py-2 px-3 text-xs text-gray-500 uppercase w-24">Kills</th>
                            <th className="text-center py-2 px-3 text-xs text-red-500 uppercase w-20">Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editingRound.rows.map((row, i) => (
                            <tr key={row.teamName} className="border-b border-gray-800/60">
                              <td className="py-2 px-3 text-white font-semibold">{row.teamName}</td>
                              <td className="py-2 px-3 text-gray-500 text-xs hidden sm:table-cell">{(row.roster || []).join(", ") || "—"}</td>
                              <td className="py-2 px-3">
                                <input type="number" min="1" placeholder="#"
                                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-sm text-center focus:outline-none focus:border-red-500"
                                  value={row.placement}
                                  onChange={(e) => setEditingRound((prev) => {
                                    const rows = [...prev.rows];
                                    rows[i] = { ...rows[i], placement: e.target.value };
                                    return { ...prev, rows };
                                  })} />
                              </td>
                              <td className="py-2 px-3">
                                <input type="number" min="0" placeholder="0"
                                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-sm text-center focus:outline-none focus:border-red-500"
                                  value={row.kills}
                                  onChange={(e) => setEditingRound((prev) => {
                                    const rows = [...prev.rows];
                                    rows[i] = { ...rows[i], kills: e.target.value };
                                    return { ...prev, rows };
                                  })} />
                              </td>
                              <td className="py-2 px-3 text-center">
                                <span className="text-red-400 font-black">
                                  {calcPoints(Number(row.placement) || 0, Number(row.kills) || 0)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button onClick={handleSaveRound}
                        className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold text-sm uppercase rounded-lg transition-colors flex items-center justify-center gap-2">
                        <FaSave size={12} /> Save Round {editingRound.roundNumber}
                      </button>
                      <button onClick={() => setEditingRound(null)} className="px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">Cancel</button>
                    </div>
                  </div>
                )}

                {/* Saved rounds */}
                {warzoneRounds.length === 0 && !editingRound && (
                  <div className="text-center py-12 bg-gray-900 border border-gray-800 rounded-xl">
                    <p className="text-gray-500 mb-3">No rounds yet.</p>
                    <p className="text-gray-600 text-sm">Add teams in the Teams tab, then click "Add Round" to enter results.</p>
                  </div>
                )}

                {warzoneRounds.map((round) => (
                  <div key={round.id} className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-bold uppercase tracking-widest text-red-500 border border-red-600/30 bg-red-600/10 px-3 py-1 rounded-full">
                        Round {round.roundNumber}
                      </span>
                      <button onClick={() => handleDeleteRound(round.id)} className="text-gray-600 hover:text-red-500 transition-colors" title="Delete round">
                        <FaTrash size={12} />
                      </button>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-800">
                          <th className="text-left py-2 text-xs text-gray-500 uppercase">Place</th>
                          <th className="text-left py-2 text-xs text-gray-500 uppercase">Team</th>
                          <th className="text-left py-2 text-xs text-gray-500 uppercase hidden sm:table-cell">Players</th>
                          <th className="text-center py-2 text-xs text-gray-500 uppercase">Kills</th>
                          <th className="text-center py-2 text-xs text-red-500 uppercase">Points</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...(round.standings || [])].sort((a, b) => a.placement - b.placement).map((entry) => (
                          <tr key={entry.teamName} className="border-b border-gray-800/40">
                            <td className="py-2 pr-3 text-gray-400 tabular-nums font-bold">{entry.placement}</td>
                            <td className="py-2 pr-3 text-white font-semibold">{entry.teamName}</td>
                            <td className="py-2 pr-3 text-gray-500 text-xs hidden sm:table-cell">{(entry.roster || []).join(", ") || "—"}</td>
                            <td className="py-2 text-center text-gray-300 tabular-nums">{entry.kills}</td>
                            <td className="py-2 text-center text-red-400 font-black tabular-nums">{entry.points}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}

          </section>
        )}
        </>)}
      </div>
    </div>
  );
}

export default AdminPage;
